'use client';

import { memo, type ReactNode } from 'react';

import type { TaggingData } from '@/context/tagging';
import { isFeaturedCarTile } from '@/helpers/carTileHelpers';
import { buildCarTitle } from '@/helpers/normalizers';
import { isCarUnavailable } from '@/helpers/statusHelpers';
import type { ActionsConfig } from '@/types/ActionsConfig';
import type { Car } from '@/types/Car';
import type { CompareConfig } from '@/types/CompareConfig';
import type { FavoritesConfig } from '@/types/FavoritesConfig';
import type { CarTileFbsInformation } from '@/types/FinanceHelperTypes';
import type { GalleryConfig } from '@/types/GalleryConfig';
import type { MenuOptions } from '@/types/MenuOptions';
import type { MonthlyPaymentConfig } from '@/types/MonthlyPaymentConfig';
import type { TileSize } from '@/types/TileSize';

import Content from '../Content/Content';
import Media from '../Media/Media';
import TaggingProvider from '../TaggingProvider/TaggingProvider';
import { Wrapper } from './CarTile.styles';

export type CarTileProps = {
  /** Data for the car that this tile displays. */
  car: Car;
  /** The size variant of the component. */
  size?: TileSize;
  /** The URL for the car's main image. */
  imageSrc: string;
  /** Used to display a special variant of the "large" size tile. */
  isFeatured?: boolean;
  /** Config for action buttons. */
  actions?: ActionsConfig;
  /** Config for the compare feature. */
  compare?: CompareConfig;
  /** Config for the image gallery. */
  gallery?: GalleryConfig;
  /** Content for the car's availability (transfer text, shipping info, store location, etc.). */
  availability?: ReactNode;
  /** The options to be displayed in the dropdown menu. */
  menuOptions?: MenuOptions;
  /** Config for the favoriting feature. */
  favorites?: FavoritesConfig;
  /** Config for the monthly payment information. */
  monthlyPayment?: MonthlyPaymentConfig;
  /** Financing information. */
  fbsInformation?: CarTileFbsInformation;
  /** Handle various tagging/analytics related events. */
  handleTagging?: (data: TaggingData) => void;
};

function CarTile({
  car,
  actions,
  gallery,
  compare,
  imageSrc,
  favorites,
  menuOptions,
  availability,
  handleTagging,
  size = 'large',
  monthlyPayment,
  fbsInformation,
  isFeatured = false,
}: CarTileProps) {
  const {
    hasPriceDrop = false,
    isComingSoon = false,
    isReserved = false,
    isPopular = false,
  } = car.statusInfo;

  const clickProps = [
    'Element type: Car Tile',
    `StockNumber: ${car.stockNumber}`,
    `YMM: ${buildCarTitle(car, false)}`,
    `Price: ${car.price}`,
    `Coming Soon: ${isComingSoon}`,
    `Reserved: ${isReserved}`,
    `Marked Down: ${hasPriceDrop}`,
    `Popular: ${isPopular}`,
  ].join(',');

  const href = `/car/${car.stockNumber}`;

  const isFeaturedTile = isFeaturedCarTile(size, isFeatured);

  const isUnavailable = isCarUnavailable(car.statusInfo);

  return (
    <TaggingProvider handler={handleTagging}>
      <Wrapper
        className="kmx-car-tile"
        component="article"
        data-clickprops={clickProps}
        data-featured={isFeaturedTile}
        data-id={car.stockNumber}
        data-price-drop={hasPriceDrop}
        data-size={size}
        data-unavailable={isUnavailable}
        elevation={isFeaturedTile ? 2 : 0}
      >
        <Media
          actions={actions}
          car={car}
          href={href}
          imageSrc={imageSrc}
          favorites={favorites}
          gallery={gallery}
          isFeatured={isFeaturedTile}
          isUnavailable={isUnavailable}
          tileSize={size}
        />
        <Content
          car={car}
          href={href}
          tileSize={size}
          compare={compare}
          actions={actions}
          menuOptions={menuOptions}
          availability={availability}
          isFeatured={isFeaturedTile}
          isUnavailable={isUnavailable}
          monthlyPayment={monthlyPayment}
          fbsInformation={fbsInformation}
        />
      </Wrapper>
    </TaggingProvider>
  );
}

export default memo(CarTile);
